import React from 'react';
import { PresetStructureId } from '../../types/simulation';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { PRESET_STRUCTURES } from '../../simulation/presets';
import { formatAE } from '../../utils/formatters';
import {
  FolderKanban,
  Users,
  CheckCircle2,
  Award,
  Crown,
  Layers,
} from 'lucide-react';

export interface PresetSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  activePresetId: PresetStructureId | null;
  onSelectPreset: (presetId: PresetStructureId) => void;
}

const PRESET_ICONS: Record<PresetStructureId, React.ReactNode> = {
  starting_trio: <Users className="w-6 h-6 text-sky-400" />,
  level_4_team: <Layers className="w-6 h-6 text-teal-400" />,
  director_structure: <Award className="w-6 h-6 text-amber-400" />,
  coowner_structure: <Crown className="w-6 h-6 text-yellow-400" />,
};

export const PresetSelectorModal: React.FC<PresetSelectorModalProps> = ({
  isOpen,
  onClose,
  activePresetId,
  onSelectPreset,
}) => {
  const handleApply = (id: PresetStructureId) => {
    onSelectPreset(id);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <FolderKanban className="w-5 h-5 text-accent-emerald" />
          <span>Готові шаблони структур команди</span>
        </div>
      }
      subtitle="Оберіть базову конфігурацію для швидкого старту моделювання в конструкторі"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PRESET_STRUCTURES.map((preset) => {
          const isActive = activePresetId === preset.id;

          return (
            <div
              key={preset.id}
              onClick={() => handleApply(preset.id)}
              className={`
                glass-card rounded-2xl p-5 border transition-all duration-200 cursor-pointer
                flex flex-col justify-between space-y-4 group
                ${
                  isActive
                    ? 'border-accent-emerald/60 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.2)]'
                    : 'border-white/10 hover:border-accent-emerald/40 hover:bg-surface-hover/60 hover:-translate-y-0.5'
                }
              `}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-surface-hover border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    {PRESET_ICONS[preset.id]}
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-text-primary group-hover:text-accent-emerald transition-colors">
                      {preset.name}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge level={preset.rootLevel as any} size="sm">
                        Рівень {preset.rootLevel}
                      </Badge>
                      <span className="text-[11px] text-text-muted">
                        {preset.teamSize} консультантів
                      </span>
                    </div>
                  </div>
                </div>

                {isActive && (
                  <span className="text-accent-neon flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5" />
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-xs text-text-secondary leading-relaxed flex-1">
                {preset.description}
              </p>

              {/* Footer Metrics & Action */}
              <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                <div className="text-xs">
                  <span className="text-text-muted">Очікуваний обіг: </span>
                  <strong className="text-text-primary font-mono font-semibold">
                    {formatAE(preset.expectedMonthlyAE, 0)}/міс
                  </strong>
                </div>

                <Button
                  variant={isActive ? 'neon' : 'secondary'}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApply(preset.id);
                  }}
                >
                  {isActive ? 'Завантажено' : 'Завантажити шаблон'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
